import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Account } from '@/features/accounts/types';
import { useUpdateAccountPassword } from '../../hooks/useUpdateAccountPassword';
import { formatError } from '@/shared/api';
import Dialog from '@/shared/components/Modal';
import ErrorAlert from '@/shared/components/ErrorAlert';
import ModalButton from '@/shared/components/ModalButton';
import FormField from '@/shared/components/FormField';
import FormInput from '@/shared/components/FormInput';

interface Props {
  account: Account;
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  password: string;
}

export default function Modal({ account, isOpen, onClose }: Props) {
  const intl = useIntl();
  const { mutateAsync, isPending } = useUpdateAccountPassword();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: { password: '' } });

  const close = () => {
    if (isPending) return;
    reset();
    onClose();
  };

  const onSubmit = async ({ password }: FormData) => {
    try {
      await mutateAsync({ accountId: account.id, password });
      reset();
      onClose();
    } catch (error) {
      setError('root', { message: formatError(error, intl.formatMessage({ id: 'accounts.editPassword.errorFallback' })) });
    }
  };

  const passwordField = register('password', {
    required: intl.formatMessage({ id: 'accounts.editPassword.required' }),
  });

  return (
    <Dialog
      isOpen={isOpen}
      onClose={close}
      title={intl.formatMessage({ id: 'accounts.editPassword.title' })}
      eyebrow={intl.formatMessage({ id: 'accounts.editPassword.eyebrow' })}
    >
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <p className="text-sm text-slate-600">
          <FormattedMessage id="accounts.editPassword.description" />
        </p>
        {errors.root && <ErrorAlert message={errors.root.message} />}
        <FormField id="edit-visa-password" labelId="accounts.editPassword.label" error={errors.password?.message}>
          <FormInput
            {...passwordField}
            id="edit-visa-password"
            type="password"
            autoComplete="new-password"
            placeholder={intl.formatMessage({ id: 'accounts.editPassword.placeholder' })}
            disabled={isPending}
            hasError={!!errors.password}
          />
        </FormField>
        <div className="flex justify-end gap-3 border-t border-slate-200/70 pt-4">
          <ModalButton type="button" onClick={close} disabled={isPending} variant="secondary">
            <FormattedMessage id="accounts.editPassword.cancel" />
          </ModalButton>
          <ModalButton type="submit" disabled={isPending}>
            <FormattedMessage id={isPending ? 'accounts.editPassword.submitting' : 'accounts.editPassword.submit'} />
          </ModalButton>
        </div>
      </form>
    </Dialog>
  );
}
